import { forwardRef } from "react";
import { getWorkspaceTimeZone } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import type { Invoice } from "@shared/schema";

export interface InvoiceViewBranding {
  invoiceTitle?: string | null;
  companyAddress?: string | null;
  workspaceName?: string | null;
  // Task #109 — workspace's own uploaded logo. When null/empty the
  // header renders cleanly with no <img> tag (no broken image, no
  // fallback to another tenant's brand).
  logoUrl?: string | null;
}

interface InvoiceViewProps {
  invoice: Invoice;
  lateFeeDescription?: string | null;
  branding?: InvoiceViewBranding | null;
}

export const InvoiceView = forwardRef<HTMLDivElement, InvoiceViewProps>(
  ({ invoice, lateFeeDescription, branding }, ref) => {
    // Task #494: Resolve the workspace's own header / footer copy here so
    // the legacy hard-coded agency-LLC / fixed-address defaults that
    // Place / Branson, MO 65616" literals can never leak into another
    // workspace's invoices. The server attaches an `invoiceBranding`
    // payload to the invoice JSON; if a caller forgot to pass it through
    // we fall through to a neutral placeholder rather than a real agency.
    const fallbackFromInvoice = (invoice as any).invoiceBranding as
      | InvoiceViewBranding
      | undefined;
    const resolvedBranding = branding ?? fallbackFromInvoice ?? null;
    const headerTitle =
      (resolvedBranding?.invoiceTitle ?? "").trim() ||
      (resolvedBranding?.workspaceName ?? "").trim() ||
      "Your Business";
    const footerAddressLines = (resolvedBranding?.companyAddress ?? "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    const formatDate = (date: Date | string | null) => {
      if (!date) return "—";
      // Parse date string carefully to avoid UTC timezone shift
      // If it's a date-only string (YYYY-MM-DD), treat it as local date
      const dateStr = typeof date === 'string' ? date : date.toISOString();
      const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) || /^\d{4}-\d{2}-\d{2}T00:00:00/.test(dateStr);
      
      if (isDateOnly) {
        // Extract just the date parts and create date without timezone shift
        const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
        const localDate = new Date(year, month - 1, day, 12, 0, 0); // Noon to avoid DST issues
        return localDate.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
      }
      
      return new Date(date).toLocaleDateString("en-US", {
        timeZone: getWorkspaceTimeZone(),
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    };

    const formatCurrency = (amount: string | number | null) => {
      if (!amount) return "$0.00";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(typeof amount === "string" ? parseFloat(amount) : amount);
    };

    const calculateLateFee = () => {
      if (invoice.status === "paid" || invoice.status === "cancelled") {
        return 0;
      }
      
      // Always use the stored lateFee value from the server
      // The server calculates late fees based on the configured tiers and timezone
      const storedFee = invoice.lateFee;
      if (storedFee === null || storedFee === undefined) return 0;
      return typeof storedFee === "string" ? parseFloat(storedFee) || 0 : storedFee;
    };

    const lateFee = calculateLateFee();
    const eventRate = typeof invoice.eventRate === "string" ? parseFloat(invoice.eventRate) : (invoice.eventRate || 0);
    const discount = typeof (invoice as any).discount === "string" ? parseFloat((invoice as any).discount) : ((invoice as any).discount || 0);
    const totalDue = eventRate - discount + lateFee;

    return (
      <div
        ref={ref}
        className="bg-white text-black p-4 sm:p-8 max-w-2xl mx-auto"
        style={{ fontFamily: "Georgia, serif" }}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">INVOICE</h1>
            <p className="text-gray-600 mt-1">#{invoice.invoiceNumber}</p>
            <div className="mt-2">
              <h2
                className="text-lg font-semibold text-gray-900"
                data-testid="text-invoice-header-company"
              >
                {headerTitle}
              </h2>
            </div>
          </div>
          {resolvedBranding?.logoUrl ? (
            <div className="text-right">
              {/* Fixed-size slot — object-contain only behaves on a replaced
                  element with explicit width AND height. Without the wrapper
                  large source images overflowed and squeezed the title. */}
              <span
                className="inline-block overflow-hidden align-top"
                style={{ width: "200px", height: "96px" }}
              >
                <img
                  src={resolvedBranding.logoUrl}
                  alt={`${headerTitle} logo`}
                  className="object-contain block"
                  style={{ width: "100%", height: "100%", objectPosition: "right top" }}
                  data-testid="img-invoice-logo"
                />
              </span>
            </div>
          ) : null}
        </div>

        <Separator className="bg-gray-300 my-6" />

        {/* Dates */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Invoice Date</p>
            <p className="font-medium">{formatDate(invoice.invoiceDate)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date of Event</p>
            <p className="font-medium">{formatDate(invoice.dateOfEvent)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Payment Terms</p>
            <p className="font-medium">
              {invoice.paymentTerms === "due_on_completion" && "Due on Completion"}
              {invoice.paymentTerms === "net_15" && "Net 15"}
              {invoice.paymentTerms === "net_30" && "Net 30"}
              {invoice.paymentTerms === "net_60" && "Net 60"}
              {!invoice.paymentTerms && "Due on Completion"}
            </p>
          </div>
        </div>

        {/* Bill To */}
        <div className="mb-8">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Bill To</p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-semibold text-lg">{invoice.accountName || "—"}</p>
            {invoice.accountAddress && (
              <p className="text-gray-600 mt-1 whitespace-pre-line">{invoice.accountAddress}</p>
            )}
          </div>
        </div>

        {/* Services */}
        <div className="mb-8">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Services</p>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-4 bg-gray-100 px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
              <div className="col-span-2">Description</div>
              <div className="text-center">Date</div>
              <div className="text-right">Amount</div>
            </div>
            <div className="grid grid-cols-4 px-4 py-4 items-center">
              <div className="col-span-2">
                <p className="font-medium">{invoice.serviceName || "Entertainment Performance"}</p>
                {invoice.bookingDetails && (
                  <p className="text-sm text-gray-600">{invoice.bookingDetails}</p>
                )}
              </div>
              <div className="text-center text-gray-600">{formatDate(invoice.dateOfEvent)}</div>
              <div className="text-right font-semibold">{formatCurrency(invoice.eventRate)}</div>
            </div>
            {lateFee > 0 && (
              <div className="grid grid-cols-4 px-4 py-4 items-center border-t border-gray-200 bg-red-50">
                <div className="col-span-2">
                  <p className="font-medium text-red-700">Late Payment Fee</p>
                  <p className="text-sm text-red-600 mt-1">
                    {lateFeeDescription || "Payment overdue - Late fees may apply"}
                  </p>
                </div>
                <div className="text-center text-red-600">—</div>
                <div className="text-right font-semibold text-red-700">{formatCurrency(lateFee)}</div>
              </div>
            )}
          </div>
        </div>

        {/* Total */}
        <div className="flex justify-end mb-6">
          <div className="w-64">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Subtotal</span>
              <span>{formatCurrency(invoice.eventRate)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between py-2">
                <span className="text-green-600">Discount</span>
                <span className="text-green-600">-{formatCurrency(discount)}</span>
              </div>
            )}
            {lateFee > 0 && (
              <div className="flex justify-between py-2">
                <span className="text-red-600">Late Fee</span>
                <span className="text-red-600">{formatCurrency(lateFee)}</span>
              </div>
            )}
            <Separator className="bg-gray-300" />
            <div className="flex justify-between py-3">
              <span className="font-semibold text-lg">Total Due</span>
              <span className={`font-bold text-xl ${lateFee > 0 ? 'text-red-700' : 'text-gray-900'}`}>
                {formatCurrency(totalDue)}
              </span>
            </div>
          </div>
        </div>

        {/* Late Fee Disclaimer */}
        <p className="text-center text-xs text-gray-500 italic mb-6">
          Invoices unpaid 60 days after the performance date may incur a late fee of $50,
          with additional fees applied monthly as allowed by law.
        </p>

        {/* Footer */}
        <Separator className="bg-gray-300 my-6" />
        <div className="text-center text-sm text-gray-600">
          <p className="font-medium">Please make all checks payable to:</p>
          {footerAddressLines.map((line, idx) => (
            <p key={idx} data-testid={`text-invoice-footer-address-${idx}`}>
              {line}
            </p>
          ))}
          <p className="mt-2 text-gray-500">Thank you for your business!</p>
        </div>
      </div>
    );
  }
);

InvoiceView.displayName = "InvoiceView";
